/* eslint-disable array-callback-return */

import React, { useEffect, useMemo, useState, useCallback, useContext } from "react";
import {
    Table,
    Segment,
    Container,
    Header,
    Form,
    Button,
    Icon,
    Input,
    Message,
    Checkbox,
    Label,
    Modal
} from "semantic-ui-react";

import { auth, db } from "../firebase/firebaseConfig";
import {
    getDocs,
    collection,
    where,
    query,
    updateDoc,
    doc,
    deleteDoc,
    writeBatch
} from "firebase/firestore";

import Swal from "sweetalert2";
import ProtocoloTestCabDB from "../firebase/ProtocoloTestCabDB";
import ProtocoloTestDB from "../firebase/ProtocoloTestDB";
import Alertas from "./Alertas";
import ExportarExcel from "../funciones/ExportarExcel";
import { UserContext } from "../context/UserContext";

const ProtocolosTest = () => {
    const user = auth.currentUser;
    const { users } = useContext(UserContext);

    const [estadoAlerta, cambiarEstadoAlerta] = useState(false);
    const [alerta, cambiarAlerta] = useState({});

    const [familia, setFamilia] = useState([]);
    const [tipo, setTipo] = useState([]);
    const [protocolCab, setProtocolCab] = useState([]);
    const [item, setItem] = useState([]);
    const [protocolo, setProtocolo] = useState([]);
    const [protocolos, setProtocolos] = useState([]);

    const [nomFamilia, setNomFamilia] = useState("");
    const [nomTipo, setNomTipo] = useState("");
    const [buscador, setBuscador] = useState("");
    const [seleccionados, setSeleccionados] = useState([]);

    const [flag, setFlag] = useState(false);

    const [guardandoCabecera, setGuardandoCabecera] = useState(false);
    const [agregandoItems, setAgregandoItems] = useState(false);
    const [confirmando, setConfirmando] = useState(false);
    const [moviendo, setMoviendo] = useState(false);

    const [showDelete, setShowDelete] = useState(false);
    const [itemDelete, setItemDelete] = useState(null);

    const now = () => new Date();

    const toast = (tipoMsg, mensaje) => {
        cambiarEstadoAlerta(true);
        cambiarAlerta({ tipo: tipoMsg, mensaje });
    };

    // =========================================================
    // FIRESTORE
    // =========================================================

    const getFamilia = useCallback(async () => {
        if (!users?.emp_id) return;

        const q = query(
            collection(db, "familias"),
            where("emp_id", "==", users.emp_id),
            where("activo", "==", true)
        );

        const snap = await getDocs(q);
        setFamilia(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    }, [users?.emp_id]);

    const getTipo = useCallback(async () => {
        if (!users?.emp_id || !nomFamilia) {
            setTipo([]);
            return;
        }

        const q = query(
            collection(db, "tipos"),
            where("emp_id", "==", users.emp_id),
            where("familia", "==", nomFamilia),
            where("activo", "==", true)
        );

        const snap = await getDocs(q);
        setTipo(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    }, [users?.emp_id, nomFamilia]);

    const consultarCabProt = useCallback(async () => {
        if (!users?.emp_id) return;

        const q = query(
            collection(db, "protocolostestcab"),
            where("emp_id", "==", users.emp_id)
        );

        const snap = await getDocs(q);
        setProtocolCab(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    }, [users?.emp_id]);

    const getItem = async () => {
        if (!users?.emp_id) return;

        const q = query(
            collection(db, "itemsst"),
            where("emp_id", "==", users.emp_id),
            where("categoria", "==", "TEST INGRESO")
        );

        const snap = await getDocs(q);
        setItem(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    };

    const consultarProtocolos = async () => {
        if (!users?.emp_id || !nomFamilia || !nomTipo) {
            setProtocolo([]);
            return;
        }

        const q = query(
            collection(db, "protocolostest"),
            where("emp_id", "==", users.emp_id),
            where("familia", "==", nomFamilia),
            where("tipo", "==", nomTipo)
        );

        const snap = await getDocs(q);
        setProtocolo(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    };

    const leerProtocolos = async () => {
        if (!users?.emp_id) return;

        const q = query(
            collection(db, "protocolostest"),
            where("emp_id", "==", users.emp_id)
        );

        const snap = await getDocs(q);
        setProtocolos(snap.docs.map((d) => ({ ...d.data(), id: d.id })));
    };

    // =========================================================
    // MEMOS
    // =========================================================

    const familiaOptions = useMemo(() => {
        return [...familia]
            .map((d) => ({
                key: d.id,
                text: d.familia,
                value: d.familia
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [familia]);

    const tipoOptions = useMemo(() => {
        return [...tipo]
            .map((d) => ({
                key: d.id,
                text: d.tipo ?? d.familia ?? "SIN_NOMBRE",
                value: d.tipo ?? d.familia ?? "SIN_NOMBRE"
            }))
            .sort((a, b) => a.text.localeCompare(b.text));
    }, [tipo]);

    const cabeceraActual = useMemo(() => {
        return protocolCab.find(
            (p) =>
                p.familia === nomFamilia &&
                p.tipo === nomTipo
        );
    }, [protocolCab, nomFamilia, nomTipo]);

    const confirmado = cabeceraActual?.confirmado === true;

    const itemsOrdenados = useMemo(() => {
        return [...item].sort((a, b) =>
            (a.nombre || "").localeCompare(b.nombre || "")
        );
    }, [item]);

    /*
     * IMPORTANTE:
     * Si TODOS tienen orden, usamos orden.
     * Si es un protocolo antiguo sin orden, mantenemos su orden histórico
     * por fecha. No escribimos nada en Firestore por el solo hecho de abrirlo.
     */
    const protocoloOrdenado = useMemo(() => {
        const lista = [...protocolo];

        const todosTienenOrden =
            lista.length > 0 &&
            lista.every((p) => typeof p.orden === "number");

        if (todosTienenOrden) {
            return lista.sort((a, b) => a.orden - b.orden);
        }

        return lista.sort((a, b) => {
            const fechaA =
                a.fechaAdd?.toDate?.()?.getTime?.() ||
                a.fechaadd?.toDate?.()?.getTime?.() ||
                a.fechamod?.toDate?.()?.getTime?.() ||
                0;

            const fechaB =
                b.fechaAdd?.toDate?.()?.getTime?.() ||
                b.fechaadd?.toDate?.()?.getTime?.() ||
                b.fechamod?.toDate?.()?.getTime?.() ||
                0;

            return fechaA - fechaB;
        });
    }, [protocolo]);

    const itemsDisponibles = useMemo(() => {
        const usados = new Set(protocolo.map((p) => p.item_id));
        return itemsOrdenados.filter((it) => !usados.has(it.id));
    }, [itemsOrdenados, protocolo]);

    const itemsFiltrados = useMemo(() => {
        const texto = buscador.trim().toLowerCase();

        if (!texto) return itemsDisponibles;

        return itemsDisponibles.filter((it) =>
            (it.nombre || "").toLowerCase().includes(texto)
        );
    }, [itemsDisponibles, buscador]);

    const todosSeleccionados =
        itemsFiltrados.length > 0 &&
        itemsFiltrados.every((it) => seleccionados.includes(it.id));

    // Estamos construyendo cuando existe una cabecera seleccionada.
    const etapaConstruccion = !!cabeceraActual;

    const estiloPanel = (activo) => ({
        border: activo
            ? "2px solid #00b5ad"
            : "1px solid #e2e8f0",
        borderRadius: 12,
        boxShadow: activo
            ? "0 0 0 1px rgba(0,181,173,.08), 0 4px 14px rgba(15,23,42,.05)"
            : "0 4px 14px rgba(15,23,42,.05)",
        marginBottom: 16,
        transition: "border-color .2s ease, box-shadow .2s ease"
    });

    // =========================================================
    // CABECERA
    // =========================================================

    const addCabProtocolo = async (ev) => {
        ev?.preventDefault?.();

        cambiarEstadoAlerta(false);
        cambiarAlerta({});

        if (!users?.emp_id) return;

        if (!nomFamilia) {
            toast("error", "Seleccione Familia");
            return;
        }

        if (!nomTipo) {
            toast("error", "Seleccione Tipo");
            return;
        }

        if (cabeceraActual) {
            toast(
                "error",
                cabeceraActual.confirmado
                    ? "Este Test de Ingreso ya existe y está confirmado."
                    : "Este Test de Ingreso ya existe como borrador."
            );
            return;
        }

        setGuardandoCabecera(true);

        try {
            await ProtocoloTestCabDB({
                nombre: `TEST INGRESO ${nomTipo}`,
                familia: nomFamilia,
                tipo: nomTipo,
                userAdd: user?.email || "",
                userMod: user?.email || "",
                fechaAdd: now(),
                fechaMod: now(),
                emp_id: users.emp_id,
                confirmado: false
            });

            toast("exito", "Test de Ingreso creado como borrador.");
            setFlag((v) => !v);
        } catch (error) {
            toast("error", String(error?.message || error));
        } finally {
            setGuardandoCabecera(false);
        }
    };

    // =========================================================
    // SELECCIÓN DE ITEMS
    // =========================================================

    const toggleSeleccion = (id) => {
        setSeleccionados((prev) =>
            prev.includes(id)
                ? prev.filter((x) => x !== id)
                : [...prev, id]
        );
    };

    const seleccionarTodos = () => {
        const ids = itemsFiltrados.map((it) => it.id);

        if (todosSeleccionados) {
            setSeleccionados((prev) =>
                prev.filter((id) => !ids.includes(id))
            );
        } else {
            setSeleccionados((prev) => [
                ...new Set([...prev, ...ids])
            ]);
        }
    };

    const agregarSeleccionados = async () => {
        cambiarEstadoAlerta(false);
        cambiarAlerta({});

        if (!nomFamilia || !nomTipo) {
            toast("error", "Seleccione Familia y Tipo.");
            return;
        }

        if (!cabeceraActual?.id) {
            toast("error", "Primero debe crear el Test de Ingreso.");
            return;
        }

        if (confirmado) {
            toast(
                "error",
                "El protocolo está confirmado y no puede modificarse."
            );
            return;
        }

        if (seleccionados.length === 0) {
            toast("error", "Seleccione al menos un ítem.");
            return;
        }

        const seleccion = itemsOrdenados.filter((it) =>
            seleccionados.includes(it.id)
        );

        if (seleccion.length === 0) return;

        setAgregandoItems(true);

        try {
            /*
             * Si el borrador es antiguo y todavía no tiene orden,
             * al agregar nuevos elementos continuamos después de
             * la cantidad actual. No tocamos confirmados antiguos.
             */
            let siguienteOrden = protocolo.reduce(
                (max, p) =>
                    typeof p.orden === "number"
                        ? Math.max(max, p.orden)
                        : max,
                protocolo.length
            );

            for (const itemSel of seleccion) {
                const yaExiste = protocolo.some(
                    (p) => p.item_id === itemSel.id
                );

                if (yaExiste) continue;

                siguienteOrden += 1;

                await ProtocoloTestDB({
                    nombre: cabeceraActual.nombre,
                    familia: cabeceraActual.familia,
                    tipo: cabeceraActual.tipo,
                    item: itemSel.nombre,
                    item_id: itemSel.id,
                    cab_id: cabeceraActual.id,
                    orden: siguienteOrden,
                    userAdd: user?.email || "",
                    userMod: user?.email || "",
                    fechaAdd: now(),
                    fechaMod: now(),
                    emp_id: users.emp_id,
                    confirmado: false
                });
            }

            toast(
                "exito",
                `${seleccion.length} ítem(s) agregado(s) correctamente.`
            );

            setSeleccionados([]);
            setBuscador("");
            setFlag((v) => !v);
        } catch (error) {
            toast("error", String(error?.message || error));
        } finally {
            setAgregandoItems(false);
        }
    };

    // =========================================================
    // ORDENAR
    // =========================================================

    /*
     * Esta función sólo se ejecuta en BORRADOR.
     *
     * Si el borrador es antiguo y no tiene "orden", en el primer
     * movimiento asignamos 1..N respetando el orden visual actual.
     *
     * Un protocolo CONFIRMADO nunca entra aquí, por lo que los
     * protocolos productivos antiguos no son modificados.
     */
    const moverItem = async (index, direccion) => {
        if (confirmado || moviendo) return;

        const nuevoIndex =
            direccion === "ARRIBA"
                ? index - 1
                : index + 1;

        if (
            nuevoIndex < 0 ||
            nuevoIndex >= protocoloOrdenado.length
        ) {
            return;
        }

        setMoviendo(true);

        try {
            const lista = protocoloOrdenado.map((p, i) => ({
                ...p,
                ordenNuevo: i + 1
            }));

            const temporal = lista[index];
            lista[index] = lista[nuevoIndex];
            lista[nuevoIndex] = temporal;

            const batch = writeBatch(db);

            /*
             * Guardamos 1..N para todo el BORRADOR.
             * Esto también normaliza de forma segura un borrador legacy.
             */
            lista.forEach((p, i) => {
                batch.update(
                    doc(db, "protocolostest", p.id),
                    {
                        orden: i + 1,
                        userMod: user?.email || "",
                        fechaMod: now()
                    }
                );
            });

            await batch.commit();

            setFlag((v) => !v);
        } catch (error) {
            toast(
                "error",
                `No fue posible cambiar el orden: ${String(
                    error?.message || error
                )}`
            );
        } finally {
            setMoviendo(false);
        }
    };

    // =========================================================
    // ELIMINAR
    // =========================================================

    const handleDelete = (id) => {
        if (confirmado) {
            toast(
                "error",
                "Un Test de Ingreso confirmado no puede modificarse."
            );
            return;
        }

        setItemDelete(id);
        setShowDelete(true);
    };

    const borrarItem = async () => {
        if (!itemDelete) return;

        try {
            await deleteDoc(
                doc(db, "protocolostest", itemDelete)
            );

            toast("exito", "Ítem eliminado correctamente.");
            setFlag((v) => !v);
        } catch (error) {
            toast("error", "Error al eliminar el ítem.");
        } finally {
            setItemDelete(null);
            setShowDelete(false);
        }
    };

    // =========================================================
    // CONFIRMAR
    // =========================================================

    const actualizarDocs = async () => {
        cambiarEstadoAlerta(false);
        cambiarAlerta({});

        if (!cabeceraActual?.id) {
            Swal.fire("Primero debe crear el Test de Ingreso.");
            return;
        }

        if (confirmado) {
            Swal.fire("Este Test de Ingreso ya está confirmado.");
            return;
        }

        if (protocolo.length === 0) {
            Swal.fire(
                "El Test de Ingreso debe contener al menos un ítem."
            );
            return;
        }

        const respuesta = await Swal.fire({
            title: "¿Confirmar Test de Ingreso?",
            html: `
                <div style="text-align:left">
                    <b>Familia:</b> ${nomFamilia}<br/>
                    <b>Tipo:</b> ${nomTipo}<br/>
                    <b>Ítems:</b> ${protocolo.length}<br/><br/>
                    Una vez confirmado quedará disponible para su uso.
                </div>
            `,
            icon: "question",
            showCancelButton: true,
            confirmButtonText: "Confirmar",
            cancelButtonText: "Cancelar"
        });

        if (!respuesta.isConfirmed) return;

        setConfirmando(true);

        try {
            await updateDoc(
                doc(
                    db,
                    "protocolostestcab",
                    cabeceraActual.id
                ),
                {
                    confirmado: true,
                    usermod: user?.email || "",
                    fechamod: now()
                }
            );

            toast(
                "exito",
                "Test de Ingreso confirmado exitosamente."
            );

            setFlag((v) => !v);
        } catch (error) {
            toast(
                "error",
                `Error al confirmar: ${String(
                    error?.message || error
                )}`
            );
        } finally {
            setConfirmando(false);
        }
    };

    // =========================================================
    // NUEVO / EDITAR / VOLVER
    // =========================================================

    const nuevo = () => {
        setNomFamilia("");
        setNomTipo("");
        setTipo([]);
        setProtocolo([]);
        setBuscador("");
        setSeleccionados([]);
    };

    const editarProtocolo = (cab) => {
        setNomFamilia(cab.familia);
        setNomTipo(cab.tipo);
        setBuscador("");
        setSeleccionados([]);
        setFlag((v) => !v);

        window.scrollTo({
            top: 0,
            behavior: "smooth"
        });
    };

    const volver = () => {
        window.history.back();
    };

    // =========================================================
    // EXCEL
    // =========================================================

    const ExportarXls = () => {
        const columnsToShow = [
            "cab_id",
            "nombre",
            "familia",
            "tipo",
            "item",
            "orden"
        ];

        const excelBlob = ExportarExcel(
            protocolos,
            columnsToShow
        );

        const excelURL = URL.createObjectURL(excelBlob);
        const downloadLink = document.createElement("a");

        downloadLink.href = excelURL;
        downloadLink.download = "testIngreso.xlsx";
        downloadLink.click();

        URL.revokeObjectURL(excelURL);
    };

    // =========================================================
    // EFFECTS
    // =========================================================

    useEffect(() => {
        if (!users?.emp_id) return;

        getFamilia();
        consultarCabProt();
    }, [users?.emp_id, getFamilia, consultarCabProt]);

    useEffect(() => {
        getTipo();
    }, [getTipo]);

    useEffect(() => {
        if (!users?.emp_id) return;

        getItem();
        consultarCabProt();
        consultarProtocolos();
        leerProtocolos();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        flag,
        users?.emp_id,
        nomFamilia,
        nomTipo
    ]);

    // =========================================================
    // UI
    // =========================================================

    return (
        <Container
            fluid
            style={{
                minHeight: "100vh",
                background: "#f8fafc",
                padding: "24px 12px 40px"
            }}
        >
            <Container
                fluid
                style={{
                    maxWidth: 1400,
                    margin: "0 auto"
                }}
            >
                {/* CABECERA */}
                <Segment
                    style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        boxShadow:
                            "0 4px 14px rgba(15,23,42,.05)",
                        padding: "18px 20px",
                        marginBottom: 16
                    }}
                >
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                            flexWrap: "wrap"
                        }}
                    >
                        <div
                            style={{
                                width: 48,
                                height: 48,
                                borderRadius: 10,
                                background: "#ecfdf5",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <Icon
                                name="clipboard outline"
                                color="teal"
                                size="large"
                                style={{ margin: 0 }}
                            />
                        </div>

                        <div style={{ flex: 1, minWidth: 220 }}>
                            <Header
                                as="h2"
                                style={{
                                    margin: 0,
                                    color: "#0f172a"
                                }}
                            >
                                Constructor de Test de Ingreso

                                <Header.Subheader
                                    style={{ marginTop: 5 }}
                                >
                                    Define las verificaciones que se
                                    aplicarán al ingreso de cada tipo
                                    de equipo.
                                </Header.Subheader>
                            </Header>
                        </div>

                        {cabeceraActual && (
                            <Label
                                color={
                                    confirmado
                                        ? "green"
                                        : "orange"
                                }
                                size="large"
                            >
                                <Icon
                                    name={
                                        confirmado
                                            ? "check circle"
                                            : "edit outline"
                                    }
                                />

                                {confirmado
                                    ? "CONFIRMADO"
                                    : "BORRADOR"}
                            </Label>
                        )}

                        <Button
                            basic
                            type="button"
                            onClick={volver}
                        >
                            <Icon name="arrow left" />
                            Volver
                        </Button>
                    </div>
                </Segment>

                {/* DEFINICIÓN */}
                <Segment
                    style={estiloPanel(!etapaConstruccion)}
                >
                    <Header
                        as="h3"
                        style={{ color: "#0f172a" }}
                    >
                        <Icon
                            name="settings"
                            color="teal"
                        />

                        <Header.Content>
                            1. Definición

                            <Header.Subheader>
                                Selecciona la familia y el tipo de
                                equipamiento.
                            </Header.Subheader>
                        </Header.Content>
                    </Header>

                    <Form size="small">
                        <Form.Group widths="equal">
                            <Form.Dropdown
                                label="Familia"
                                placeholder="Seleccione Familia"
                                fluid
                                search
                                selection
                                options={familiaOptions}
                                value={nomFamilia || ""}
                                onChange={(e, { value }) => {
                                    setNomFamilia(value);
                                    setNomTipo("");
                                    setProtocolo([]);
                                    setSeleccionados([]);
                                }}
                            />

                            <Form.Dropdown
                                label="Tipo de equipamiento"
                                placeholder="Seleccione Tipo"
                                fluid
                                search
                                selection
                                disabled={!nomFamilia}
                                options={tipoOptions}
                                value={nomTipo || ""}
                                onChange={(e, { value }) => {
                                    setNomTipo(value);
                                    setSeleccionados([]);
                                    setBuscador("");
                                }}
                            />
                        </Form.Group>

                        {nomFamilia && nomTipo && (
                            <Segment
                                secondary
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent:
                                        "space-between",
                                    gap: 12,
                                    flexWrap: "wrap"
                                }}
                            >
                                <div>
                                    <div
                                        style={{
                                            fontWeight: 700,
                                            color: "#0f172a"
                                        }}
                                    >
                                        TEST INGRESO {nomTipo}
                                    </div>

                                    <div
                                        style={{
                                            color: "#64748b",
                                            fontSize: 12,
                                            marginTop: 3
                                        }}
                                    >
                                        {nomFamilia} / {nomTipo}
                                    </div>
                                </div>

                                {!cabeceraActual ? (
                                    <Button
                                        color="teal"
                                        type="button"
                                        loading={guardandoCabecera}
                                        disabled={guardandoCabecera}
                                        onClick={addCabProtocolo}
                                    >
                                        <Icon name="plus" />
                                        Crear Test
                                    </Button>
                                ) : (
                                    <Label
                                        basic
                                        color={
                                            confirmado
                                                ? "green"
                                                : "orange"
                                        }
                                    >
                                        <Icon
                                            name={
                                                confirmado
                                                    ? "lock"
                                                    : "edit"
                                            }
                                        />

                                        {confirmado
                                            ? "Protocolo confirmado"
                                            : "Protocolo en edición"}
                                    </Label>
                                )}
                            </Segment>
                        )}

                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                marginTop: 10
                            }}
                        >
                            <Button
                                basic
                                type="button"
                                onClick={nuevo}
                            >
                                <Icon name="file outline" />
                                Nuevo
                            </Button>
                        </div>
                    </Form>
                </Segment>

                {/* CONSTRUCTOR */}
                <Segment
                    style={estiloPanel(etapaConstruccion)}
                >
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                            marginBottom: 16
                        }}
                    >
                        <Header
                            as="h3"
                            style={{ margin: 0 }}
                        >
                            <Icon
                                name="tasks"
                                color="teal"
                            />

                            <Header.Content>
                                2. Construcción del Test

                                <Header.Subheader>
                                    Selecciona y ordena los ítems que
                                    formarán parte del protocolo.
                                </Header.Subheader>
                            </Header.Content>
                        </Header>

                        <div>
                            <Label basic color="grey">
                                Disponibles

                                <Label.Detail>
                                    {itemsDisponibles.length}
                                </Label.Detail>
                            </Label>

                            <Label basic color="teal">
                                En el test

                                <Label.Detail>
                                    {protocolo.length}
                                </Label.Detail>
                            </Label>
                        </div>
                    </div>

                    {!nomTipo && (
                        <Message info>
                            <Icon name="info circle" />
                            Selecciona una Familia y un Tipo de
                            equipamiento para comenzar.
                        </Message>
                    )}

                    {nomTipo && !cabeceraActual && (
                        <Message warning>
                            <Icon name="warning sign" />
                            Primero crea la cabecera del Test de
                            Ingreso para poder agregar ítems.
                        </Message>
                    )}

                    {confirmado && (
                        <Message positive>
                            <Icon name="lock" />
                            Este Test de Ingreso está confirmado.
                            Puedes revisarlo, pero ya no puede ser
                            modificado.
                        </Message>
                    )}

                    {cabeceraActual && (
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns:
                                    "minmax(0,1fr) minmax(0,1fr)",
                                gap: 16
                            }}
                        >
                            {/* DISPONIBLES */}
                            <Segment
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    overflow: "hidden",
                                    border:
                                        "1px solid #e2e8f0"
                                }}
                            >
                                <div
                                    style={{
                                        padding: 14,
                                        borderBottom:
                                            "1px solid #e2e8f0"
                                    }}
                                >
                                    <Header
                                        as="h4"
                                        style={{
                                            marginBottom: 10
                                        }}
                                    >
                                        Ítems disponibles
                                    </Header>

                                    <Input
                                        fluid
                                        size="small"
                                        icon="search"
                                        placeholder="Buscar ítem..."
                                        value={buscador}
                                        disabled={confirmado}
                                        onChange={(e) =>
                                            setBuscador(
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>

                                <div
                                    style={{
                                        maxHeight: 420,
                                        overflowY: "auto"
                                    }}
                                >
                                    <Table
                                        compact
                                        selectable
                                        very
                                        basic
                                        style={{ margin: 0 }}
                                    >
                                        <Table.Header
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                                background:
                                                    "#f8fafc"
                                            }}
                                        >
                                            <Table.Row>
                                                <Table.HeaderCell
                                                    width={1}
                                                    textAlign="center"
                                                >
                                                    <Checkbox
                                                        checked={
                                                            todosSeleccionados
                                                        }
                                                        disabled={
                                                            confirmado ||
                                                            itemsFiltrados.length ===
                                                                0
                                                        }
                                                        onChange={
                                                            seleccionarTodos
                                                        }
                                                    />
                                                </Table.HeaderCell>

                                                <Table.HeaderCell>
                                                    Ítem
                                                </Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>

                                        <Table.Body>
                                            {itemsFiltrados.map(
                                                (it) => (
                                                    <Table.Row
                                                        key={it.id}
                                                        onClick={() =>
                                                            !confirmado &&
                                                            toggleSeleccion(
                                                                it.id
                                                            )
                                                        }
                                                        style={{
                                                            cursor:
                                                                confirmado
                                                                    ? "default"
                                                                    : "pointer"
                                                        }}
                                                    >
                                                        <Table.Cell textAlign="center">
                                                            <Checkbox
                                                                checked={seleccionados.includes(
                                                                    it.id
                                                                )}
                                                                disabled={
                                                                    confirmado
                                                                }
                                                                onChange={() =>
                                                                    toggleSeleccion(
                                                                        it.id
                                                                    )
                                                                }
                                                                onClick={(
                                                                    e
                                                                ) =>
                                                                    e.stopPropagation()
                                                                }
                                                            />
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            style={{
                                                                whiteSpace:
                                                                    "normal"
                                                            }}
                                                        >
                                                            {it.nombre}
                                                        </Table.Cell>
                                                    </Table.Row>
                                                )
                                            )}

                                            {itemsFiltrados.length ===
                                                0 && (
                                                <Table.Row>
                                                    <Table.Cell
                                                        colSpan="2"
                                                        textAlign="center"
                                                        style={{
                                                            padding: 25,
                                                            color:
                                                                "#64748b"
                                                        }}
                                                    >
                                                        <Icon name="inbox" />
                                                        No hay ítems
                                                        disponibles.
                                                    </Table.Cell>
                                                </Table.Row>
                                            )}
                                        </Table.Body>
                                    </Table>
                                </div>

                                {!confirmado && (
                                    <div
                                        style={{
                                            padding: 12,
                                            borderTop:
                                                "1px solid #e2e8f0",
                                            display: "flex",
                                            justifyContent:
                                                "space-between",
                                            alignItems: "center",
                                            gap: 10
                                        }}
                                    >
                                        <span
                                            style={{
                                                color: "#64748b",
                                                fontSize: 12
                                            }}
                                        >
                                            {seleccionados.length}{" "}
                                            seleccionado(s)
                                        </span>

                                        <Button
                                            color="teal"
                                            size="small"
                                            loading={
                                                agregandoItems
                                            }
                                            disabled={
                                                agregandoItems ||
                                                seleccionados.length ===
                                                    0
                                            }
                                            onClick={
                                                agregarSeleccionados
                                            }
                                        >
                                            <Icon name="arrow right" />
                                            Agregar
                                        </Button>
                                    </div>
                                )}
                            </Segment>

                            {/* TEST ACTUAL */}
                            <Segment
                                style={{
                                    margin: 0,
                                    padding: 0,
                                    overflow: "hidden",
                                    border:
                                        "1px solid #e2e8f0"
                                }}
                            >
                                <div
                                    style={{
                                        padding: 14,
                                        borderBottom:
                                            "1px solid #e2e8f0",
                                        display: "flex",
                                        justifyContent:
                                            "space-between",
                                        alignItems: "center"
                                    }}
                                >
                                    <Header
                                        as="h4"
                                        style={{ margin: 0 }}
                                    >
                                        Test de Ingreso
                                    </Header>

                                    <Label
                                        circular
                                        color="teal"
                                    >
                                        {protocolo.length}
                                    </Label>
                                </div>

                                <div
                                    style={{
                                        maxHeight: 420,
                                        overflowY: "auto"
                                    }}
                                >
                                    <Table
                                        compact
                                        striped
                                        selectable
                                        style={{ margin: 0 }}
                                    >
                                        <Table.Header
                                            style={{
                                                position: "sticky",
                                                top: 0,
                                                zIndex: 2,
                                                background:
                                                    "#f8fafc"
                                            }}
                                        >
                                            <Table.Row>
                                                <Table.HeaderCell
                                                    width={2}
                                                    textAlign="center"
                                                >
                                                    Orden
                                                </Table.HeaderCell>

                                                <Table.HeaderCell>
                                                    Ítem
                                                </Table.HeaderCell>

                                                <Table.HeaderCell
                                                    width={3}
                                                    textAlign="center"
                                                >
                                                    Mover
                                                </Table.HeaderCell>

                                                <Table.HeaderCell
                                                    width={2}
                                                    textAlign="center"
                                                >
                                                    Eliminar
                                                </Table.HeaderCell>
                                            </Table.Row>
                                        </Table.Header>

                                        <Table.Body>
                                            {protocoloOrdenado.map(
                                                (p, index) => (
                                                    <Table.Row
                                                        key={p.id}
                                                    >
                                                        <Table.Cell textAlign="center">
                                                            <Label
                                                                circular
                                                                basic
                                                                color="teal"
                                                            >
                                                                {index +
                                                                    1}
                                                            </Label>
                                                        </Table.Cell>

                                                        <Table.Cell
                                                            style={{
                                                                whiteSpace:
                                                                    "normal",
                                                                fontWeight: 500
                                                            }}
                                                        >
                                                            {p.item}
                                                        </Table.Cell>

                                                        <Table.Cell textAlign="center">
                                                            <Button.Group
                                                                size="mini"
                                                            >
                                                                <Button
                                                                    basic
                                                                    compact
                                                                    icon
                                                                    disabled={
                                                                        confirmado ||
                                                                        moviendo ||
                                                                        index ===
                                                                            0
                                                                    }
                                                                    title="Subir"
                                                                    onClick={() =>
                                                                        moverItem(
                                                                            index,
                                                                            "ARRIBA"
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        name="arrow up"
                                                                        style={{
                                                                            margin: 0
                                                                        }}
                                                                    />
                                                                </Button>

                                                                <Button
                                                                    basic
                                                                    compact
                                                                    icon
                                                                    disabled={
                                                                        confirmado ||
                                                                        moviendo ||
                                                                        index ===
                                                                            protocoloOrdenado.length -
                                                                                1
                                                                    }
                                                                    title="Bajar"
                                                                    onClick={() =>
                                                                        moverItem(
                                                                            index,
                                                                            "ABAJO"
                                                                        )
                                                                    }
                                                                >
                                                                    <Icon
                                                                        name="arrow down"
                                                                        style={{
                                                                            margin: 0
                                                                        }}
                                                                    />
                                                                </Button>
                                                            </Button.Group>
                                                        </Table.Cell>

                                                        <Table.Cell textAlign="center">
                                                            <Button
                                                                basic
                                                                compact
                                                                icon
                                                                size="mini"
                                                                disabled={
                                                                    confirmado
                                                                }
                                                                title={
                                                                    confirmado
                                                                        ? "Protocolo confirmado"
                                                                        : "Eliminar ítem"
                                                                }
                                                                onClick={() =>
                                                                    handleDelete(
                                                                        p.id
                                                                    )
                                                                }
                                                                style={{
                                                                    color:
                                                                        "#db2828",
                                                                    background:
                                                                        "#fff",
                                                                    boxShadow:
                                                                        "none"
                                                                }}
                                                            >
                                                                <Icon
                                                                    name="trash alternate outline"
                                                                    color={
                                                                        confirmado
                                                                            ? "grey"
                                                                            : "red"
                                                                    }
                                                                    style={{
                                                                        margin: 0
                                                                    }}
                                                                />
                                                            </Button>
                                                        </Table.Cell>
                                                    </Table.Row>
                                                )
                                            )}

                                            {protocoloOrdenado.length ===
                                                0 && (
                                                <Table.Row>
                                                    <Table.Cell
                                                        colSpan="4"
                                                        textAlign="center"
                                                        style={{
                                                            padding: 30,
                                                            color:
                                                                "#64748b"
                                                        }}
                                                    >
                                                        <Icon
                                                            name="clipboard outline"
                                                            size="large"
                                                            color="grey"
                                                        />

                                                        <div
                                                            style={{
                                                                marginTop: 7
                                                            }}
                                                        >
                                                            Aún no has
                                                            agregado
                                                            ítems.
                                                        </div>
                                                    </Table.Cell>
                                                </Table.Row>
                                            )}
                                        </Table.Body>
                                    </Table>
                                </div>
                            </Segment>
                        </div>
                    )}

                    {cabeceraActual && !confirmado && (
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                gap: 10,
                                marginTop: 16
                            }}
                        >
                            <span
                                style={{
                                    color: "#64748b",
                                    fontSize: 12
                                }}
                            >
                                {protocolo.length} ítem(s) en el
                                Test de Ingreso
                            </span>

                            <Button
                                color="teal"
                                disabled={
                                    protocolo.length === 0 ||
                                    confirmando ||
                                    moviendo
                                }
                                loading={confirmando}
                                onClick={actualizarDocs}
                            >
                                <Icon name="check circle" />
                                Confirmar Test de Ingreso
                            </Button>
                        </div>
                    )}
                </Segment>

                {/* PROTOCOLOS CREADOS */}
                <Segment
                    style={{
                        border: "1px solid #e2e8f0",
                        borderRadius: 12,
                        boxShadow:
                            "0 4px 14px rgba(15,23,42,.05)",
                        padding: 0,
                        overflow: "hidden"
                    }}
                >
                    <div
                        style={{
                            padding: "14px 16px",
                            borderBottom:
                                "1px solid #e2e8f0",
                            display: "flex",
                            justifyContent:
                                "space-between",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap"
                        }}
                    >
                        <Header
                            as="h3"
                            style={{ margin: 0 }}
                        >
                            <Icon
                                name="list alternate outline"
                                color="teal"
                            />

                            <Header.Content>
                                Test de Ingreso creados

                                <Header.Subheader>
                                    Protocolos disponibles por
                                    familia y tipo de equipo.
                                </Header.Subheader>
                            </Header.Content>
                        </Header>

                        <Button
                            basic
                            color="green"
                            size="small"
                            onClick={ExportarXls}
                        >
                            <Icon name="file excel" />
                            Exportar Excel
                        </Button>
                    </div>

                    <div
                        style={{
                            maxHeight: 500,
                            overflow: "auto"
                        }}
                    >
                        <Table
                            celled
                            striped
                            selectable
                            compact="very"
                            style={{
                                margin: 0,
                                minWidth: 800
                            }}
                        >
                            <Table.Header
                                style={{
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 2,
                                    background: "#f8fafc"
                                }}
                            >
                                <Table.Row>
                                    <Table.HeaderCell
                                        width={1}
                                        textAlign="center"
                                    >
                                        #
                                    </Table.HeaderCell>

                                    <Table.HeaderCell>
                                        Nombre
                                    </Table.HeaderCell>

                                    <Table.HeaderCell>
                                        Familia
                                    </Table.HeaderCell>

                                    <Table.HeaderCell>
                                        Tipo
                                    </Table.HeaderCell>

                                    <Table.HeaderCell textAlign="center">
                                        Estado
                                    </Table.HeaderCell>

                                    <Table.HeaderCell
                                        width={2}
                                        textAlign="center"
                                    >
                                        Acción
                                    </Table.HeaderCell>
                                </Table.Row>
                            </Table.Header>

                            <Table.Body>
                                {protocolCab.map(
                                    (cab, index) => (
                                        <Table.Row
                                            key={
                                                cab.id ??
                                                index
                                            }
                                        >
                                            <Table.Cell textAlign="center">
                                                {index + 1}
                                            </Table.Cell>

                                            <Table.Cell
                                                style={{
                                                    fontWeight: 600,
                                                    whiteSpace:
                                                        "normal"
                                                }}
                                            >
                                                {cab.nombre}
                                            </Table.Cell>

                                            <Table.Cell>
                                                {cab.familia}
                                            </Table.Cell>

                                            <Table.Cell>
                                                {cab.tipo}
                                            </Table.Cell>

                                            <Table.Cell textAlign="center">
                                                <Label
                                                    basic
                                                    color={
                                                        cab.confirmado
                                                            ? "green"
                                                            : "orange"
                                                    }
                                                    size="tiny"
                                                >
                                                    <Icon
                                                        name={
                                                            cab.confirmado
                                                                ? "check circle"
                                                                : "edit outline"
                                                        }
                                                    />

                                                    {cab.confirmado
                                                        ? "CONFIRMADO"
                                                        : "BORRADOR"}
                                                </Label>
                                            </Table.Cell>

                                            <Table.Cell textAlign="center">
                                                <Button
                                                    basic
                                                    compact
                                                    icon
                                                    size="mini"
                                                    color="teal"
                                                    title={
                                                        cab.confirmado
                                                            ? "Ver Test"
                                                            : "Editar Test"
                                                    }
                                                    onClick={() =>
                                                        editarProtocolo(
                                                            cab
                                                        )
                                                    }
                                                >
                                                    <Icon
                                                        name={
                                                            cab.confirmado
                                                                ? "eye"
                                                                : "edit outline"
                                                        }
                                                        style={{
                                                            margin: 0
                                                        }}
                                                    />
                                                </Button>
                                            </Table.Cell>
                                        </Table.Row>
                                    )
                                )}

                                {protocolCab.length === 0 && (
                                    <Table.Row>
                                        <Table.Cell
                                            colSpan="6"
                                            textAlign="center"
                                            style={{
                                                padding: 30,
                                                color: "#64748b"
                                            }}
                                        >
                                            No hay Test de
                                            Ingreso creados.
                                        </Table.Cell>
                                    </Table.Row>
                                )}
                            </Table.Body>
                        </Table>
                    </div>
                </Segment>

                {/* ELIMINAR */}
                <Modal
                    size="tiny"
                    open={showDelete}
                    onClose={() =>
                        setShowDelete(false)
                    }
                >
                    <Modal.Header>
                        Eliminar ítem
                    </Modal.Header>

                    <Modal.Content>
                        <p>
                            ¿Estás seguro de que deseas eliminar
                            este ítem del Test de Ingreso?
                        </p>
                    </Modal.Content>

                    <Modal.Actions>
                        <Button
                            basic
                            onClick={() => {
                                setItemDelete(null);
                                setShowDelete(false);
                            }}
                        >
                            Cancelar
                        </Button>

                        <Button
                            color="red"
                            onClick={borrarItem}
                        >
                            <Icon name="trash" />
                            Eliminar
                        </Button>
                    </Modal.Actions>
                </Modal>

                <Alertas
                    tipo={alerta.tipo}
                    mensaje={alerta.mensaje}
                    estadoAlerta={estadoAlerta}
                    cambiarEstadoAlerta={
                        cambiarEstadoAlerta
                    }
                />
            </Container>
        </Container>
    );
};

export default ProtocolosTest;